// Decoupage report (screenplay): one row per scene with parsed heading
// (kind/location/time) plus the set of characters referenced inside the
// chapter body via `@@CHR NAME` cues. Emits a CSV download.

const SCENE_RE = /^(INT|EXT|INT\/EXT|I\/E)\.?\s+(.+?)\s*-\s*(.+?)\s*$/i;
const CHR_PREFIX_RE = /^[\t ]*@@CHR\s+(.+?)\s*$/gim;
const CHR_INLINE_RE = /<char(?:\s[^>]*)?>\s*([^<]+?)\s*<\/char>/gi;

function parseSceneTitle(title) {
  if (!title) return null;
  const m = SCENE_RE.exec(String(title).trim());
  if (!m) return null;
  return {
    kind: m[1].toUpperCase(),
    location: m[2].trim().toUpperCase(),
    time: m[3].trim().toUpperCase(),
  };
}

function extractCharacters(text) {
  const names = new Set();
  if (!text) return [];
  CHR_PREFIX_RE.lastIndex = 0;
  let m;
  while ((m = CHR_PREFIX_RE.exec(text)) !== null) {
    const n = m[1].trim().toUpperCase();
    if (n) names.add(n);
  }
  CHR_INLINE_RE.lastIndex = 0;
  while ((m = CHR_INLINE_RE.exec(text)) !== null) {
    const n = m[1].trim().toUpperCase();
    if (n) names.add(n);
  }
  return Array.from(names).sort();
}

// RFC 4180 quoting: wrap in quotes whenever the cell has a quote, comma, or
// newline; double up internal quotes.
function csvCell(v) {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildDecoupageCSV(chapters, contents = {}) {
  const header = ['scene_num', 'title', 'kind', 'location', 'time', 'description', 'character_count', 'characters'];
  const rows = [header];
  chapters.forEach((ch, i) => {
    const parsed = parseSceneTitle(ch.title) || { kind: '', location: '', time: '' };
    const chars = extractCharacters(contents[ch.id] || '');
    rows.push([
      i + 1,
      ch.title || '',
      parsed.kind,
      parsed.location,
      parsed.time,
      ch.description || '',
      chars.length,
      chars.join(', '),
    ]);
  });
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

// Slug the codex title for the filename. Falls back to "decoupage".
function slug(title) {
  const s = String(title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'decoupage';
}

export function downloadDecoupageCSV(codexTitle, chapters, contents) {
  const csv = buildDecoupageCSV(chapters, contents);
  // Prepend UTF-8 BOM so Excel detects the encoding correctly.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug(codexTitle)}-decoupage.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
